import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { loginSchema } from "../lib/validators";
import type { z } from "zod";
import { Input } from "../components/ui/Input";
import { Label } from "../components/ui/Label";
import { Button } from "../components/ui/Button";
import { Spinner } from "../components/ui/Spinner";
import { apiClient } from "../lib/apiClient";
import { useAuthStore } from "../store/auth";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";

type LoginFormValues = z.infer<typeof loginSchema>;

export const LoginPage = () => {
  const setAgent = useAuthStore((state) => state.setAgent);
  const status = useAuthStore((state) => state.status);
  const setStatus = useAuthStore((state) => state.setStatus);
  const agent = useAuthStore((state) => state.agent);
  const navigate = useNavigate();

  useEffect(() => {
    if (status === "authenticated" && agent) {
      navigate("/dashboard/overview", { replace: true });
    }
  }, [status, agent, navigate]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" }
  });

  const onSubmit = async (values: LoginFormValues) => {
    setStatus("loading");
    try {
      const { data } = await apiClient.post("/auth/login", values);
      setAgent(data.agent);
      toast.success("Welcome back!");
      navigate("/dashboard/overview", { replace: true });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Login failed");
      setStatus("unauthenticated");
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950 px-4">
      <div className="w-full max-w-md rounded-2xl border border-slate-900 bg-slate-950/90 p-8 shadow-xl backdrop-blur">
        <div className="mb-8 space-y-2 text-center">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-brand text-xl font-bold text-white">
            RW
          </div>
          <h1 className="text-2xl font-semibold text-slate-100">Sign in to RWASupport</h1>
          <p className="text-sm text-slate-400">
            Access the multi-agent console with enterprise-grade security.
          </p>
        </div>
        <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-2">
            <Label htmlFor="email">Email address</Label>
            <Input
              id="email"
              type="email"
              placeholder="agent@rwasupport.io"
              autoComplete="email"
              {...register("email")}
            />
            {errors.email ? (
              <p className="text-xs text-red-400">{errors.email.message}</p>
            ) : null}
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              placeholder="••••••••"
              autoComplete="current-password"
              {...register("password")}
            />
            {errors.password ? (
              <p className="text-xs text-red-400">{errors.password.message}</p>
            ) : null}
          </div>
          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Spinner size="sm" /> Signing in...
              </>
            ) : (
              "Sign in"
            )}
          </Button>
        </form>
        <div className="mt-6 rounded-lg border border-slate-900 bg-slate-900/80 p-4 text-xs text-slate-400">
          <p className="font-medium text-slate-200">Need initial access?</p>
          <p>
            Ask your platform owner to run the bootstrap command or provision a new agent from the
            directory.
          </p>
        </div>
      </div>
    </div>
  );
};
